import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useCallback,
  memo,
} from "react";

import { Link } from "react-router-dom";
import { InlineIconButton } from "../components/Forms";

import {
  Plus,
  Church as ChurchIcon,
  ListOrdered,
  ArrowUp,
  ArrowDown,
  MapPinned,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

import { toast } from "@/lib/toast";

import { useDistrictGuard } from "../hooks/useDistrictGuard";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

import {
  getChurches,
  updateChurchesOrder,
  getChurch,
  localCache,
} from "../lib/api";

import TopBar from "../components/TopBar";
import SearchInput from "../components/SearchInput";
import { useIsMounted } from "../hooks/useIsMounted";
import DomainHero from "../components/shared/DomainHero";
import EmptyState from "../components/shared/EmptyState";
import { SkeletonListItem } from "../components/shared/Skeletons";
import { normalizeText } from "../lib/search-rank";

const STATUS_LABELS = {
  organizada: "Iglesia Organizada",
  escuela_sabatica:
    "Escuela Sabática Organizada",
  filial: "Filial o Grupo",
};

export default function Churches() {
  const {
    districtId,
    ready,
    district,
    coordinatorChurchId,
  } = useDistrictGuard();

  const [churches, setChurches] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState(false);

  const [editMode, setEditMode] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    try {
      const saved = sessionStorage.getItem("keves:churches:scrollY");
      if (saved) { window.scrollTo(0, parseInt(saved, 10)); sessionStorage.removeItem("keves:churches:scrollY"); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    return () => { try { sessionStorage.setItem("keves:churches:scrollY", String(Math.round(window.scrollY))); } catch { /* ignore */ } };
  }, []);

  /* ============================= */
  /* LOAD */
  /* ============================= */

  const loadChurches = useCallback(async () => {
    if (!districtId) {
      setChurches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getChurches(districtId);
      if (!isMounted()) return;
      const sorted = [...(data || [])].sort(
        (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
      );
      setChurches(sorted);
    } catch {
      if (isMounted()) {
        setLoadError(true);
        setChurches([]);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [districtId, isMounted]);

  useEffect(() => {
    if (!ready) return;
    loadChurches();
  }, [ready, loadChurches]);

  const { containerRef, pullProgress, refreshing } = usePullToRefresh(loadChurches);

  /* ============================= */
  /* PRELOAD */
  /* ============================= */

  const handlePreload = useCallback(
    async (churchId) => {
      try {
        const cached =
          await localCache.get(
            `church_${churchId}`
          );

        if (cached) return;

        const data =
          await getChurch(churchId);

        if (data) {
          await localCache.set(
            `church_${churchId}`,
            data
          );
        }
      } catch {
        // preload is best-effort; silently ignore
      }
    },
    []
  );

  /* ============================= */
  /* REORDER */
  /* ============================= */

  const handleMoveChurch =
    useCallback(
      async (index, direction) => {
        const targetIndex =
          index + direction;

        if (
          targetIndex < 0 ||
          targetIndex >= churches.length
        ) {
          return;
        }

        const previous = churches;

        const updated = [...churches];

        [
          updated[index],
          updated[targetIndex],
        ] = [
          updated[targetIndex],
          updated[index],
        ];

        setChurches(updated);

        const payload = updated.map(
          (c, i) => ({
            id: c.id,
            order_index: i,
          })
        );

        try {
          await updateChurchesOrder(
            payload
          );
        } catch {
          setChurches(previous);

          toast.error(
            "Error al guardar el nuevo orden"
          );
        }
      },
      [churches]
    );

  /* ============================= */
  /* MEMOS */
  /* ============================= */

  // Coordinators see only their assigned church; pastors see all.
  const visibleChurches = useMemo(
    () => coordinatorChurchId
      ? churches.filter((c) => c.id === coordinatorChurchId)
      : churches,
    [churches, coordinatorChurchId]
  );

  const hasChurches =
    visibleChurches.length > 0;

  // Reorder button only makes sense for pastors with multiple churches.
  const showEditButton =
    !coordinatorChurchId && visibleChurches.length > 1;

  const heroCount = useMemo(
    () => visibleChurches.length,
    [visibleChurches]
  );

  const filteredChurches = useMemo(() => {
    if (!search.trim()) return visibleChurches;
    const q = normalizeText(search);
    return visibleChurches.filter((c) =>
      normalizeText(c.name).includes(q) ||
      normalizeText(STATUS_LABELS[c.status] || c.status || "").includes(q)
    );
  }, [visibleChurches, search]);

  /* ============================= */
  /* RENDER */
  /* ============================= */

  return (
    <div ref={containerRef} className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))]">

      {/* PULL INDICATOR */}
      {(pullProgress > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden"
          style={{
            height: `${Math.min(pullProgress, 1) * 44}px`,
            opacity: Math.min(pullProgress * 1.4, 1),
            transition: refreshing ? "none" : "height 150ms ease-out, opacity 150ms ease-out",
          }}
        >
          <RefreshCw
            size={16}
            className={`text-slate-400 dark:text-slate-500 ${refreshing ? "animate-spin" : ""}`}
            style={{
              transform: `rotate(${Math.min(pullProgress, 1) * 220}deg) scale(${0.7 + Math.min(pullProgress, 1) * 0.3})`,
              transition: refreshing ? "none" : "transform 100ms ease-out",
            }}
          />
        </div>
      )}

      <TopBar
        title="Iglesias"
        subtitle={district?.name}
        safeFallback="/district"
        right={
          <div className="flex items-center gap-2">
            {showEditButton && (
              <InlineIconButton
                icon={ListOrdered}
                onClick={() => setEditMode((v) => !v)}
                aria-label="Reordenar iglesias"
                title="Reordenar iglesias"
                accent={editMode ? "indigo" : "navy"}
              />
            )}

            {!coordinatorChurchId && (
              <InlineIconButton icon={Plus} to="/churches/new" accent="navy" aria-label="Nueva iglesia" />
            )}
          </div>
        }
      />

      {/* INLINE SEARCH */}
      <div className="px-4 pt-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar iglesia..."
        />
      </div>

      {/* HERO — always rendered (loading or loaded+hasChurches) to prevent layout shift */}
      {(loading || hasChurches) && (
        <DomainHero
          domain="organization"
          title="Iglesias"
          subtitle={district?.name || "Distrito"}
          icon={<MapPinned size={20} />}
          variant="compact"
          titleClassName="truncate"
          actions={
            <div className="text-right shrink-0 flex flex-col justify-center h-10 sm:h-12">
              {loading
                ? <span className="inline-block h-6 w-6 rounded-full bg-white/20 animate-pulse mx-auto" />
                : <p className="text-[22px] font-bold leading-none text-white">{heroCount}</p>
              }
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/60 mt-1">Iglesias</p>
            </div>
          }
        />
      )}

      <div className="px-4 pt-3 pb-5 space-y-3">
        {/* LOADING */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <SkeletonListItem key={i} />)}
          </div>
        )}

        {/* ERROR */}
        {!loading && loadError && (
          <EmptyState
            icon={<AlertTriangle size={28} />}
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            actionOnClick={loadChurches}
          />
        )}

        {/* EMPTY */}
        {!loading && !loadError &&
          !hasChurches && (
            <div className="animate-fade text-center py-16 px-6">
              <div className="h-14 w-14 bg-[#1b4f9c]/8 dark:bg-blue-900/20 rounded-[20px] flex items-center justify-center mx-auto mb-4">
                <ChurchIcon size={28} className="text-[#1b4f9c] dark:text-blue-400" />
              </div>

              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1.5 text-[16px]">
                Sin iglesias registradas
              </h3>

              <p className="text-[13px] text-slate-400 dark:text-slate-500 mb-6 leading-relaxed">
                Agrega la primera congregación de tu distrito para comenzar el seguimiento pastoral.
              </p>

              <Link
                to="/churches/new"
                className="inline-flex items-center gap-2 bg-[#1b4f9c] text-white rounded-[20px] px-6 py-3 font-semibold text-[15px] shadow-[0_4px_12px_rgba(27,79,156,0.25)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
              >
                <Plus size={16} />
                Crear iglesia
              </Link>
            </div>
          )}

        {/* LIST */}
        {!loading &&
          hasChurches && (
            <>
              <div className="flex items-center gap-2 mb-2.5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">
                  {search.trim()
                    ? `${filteredChurches.length} resultado${filteredChurches.length !== 1 ? "s" : ""}`
                    : `${churches.length} iglesia${churches.length !== 1 ? "s" : ""}`}
                </p>
                <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
              </div>

              <div className="space-y-3">
                {filteredChurches.map(
                  (c, idx) => (
                    <ChurchCard
                      key={c.id}
                      church={c}
                      index={idx}
                      total={
                        churches.length
                      }
                      editMode={
                        editMode
                      }
                      onMove={
                        handleMoveChurch
                      }
                      onPreload={
                        handlePreload
                      }
                    />
                  )
                )}
                {filteredChurches.length === 0 && (
                  <EmptyState compact icon={<ChurchIcon size={20} />} title={`Sin resultados para "${search}"`} />
                )}
              </div>
            </>
          )}
      </div>

    </div>
  );
}

/* ============================= */
/* CARD */
/* ============================= */

const ChurchCard = memo(
  function ChurchCard({
    church,
    index,
    total,
    editMode,
    onMove,
    onPreload,
  }) {
    const isFirst = index === 0;

    const isLast =
      index === total - 1;

    return (
      <div
        className="list-item-lazy flex items-center bg-white dark:bg-slate-800 rounded-[24px] p-4 border border-slate-200/50 dark:border-slate-700/50 shadow-card transition-all duration-300 ease-out hover:shadow-card-hover group"
        style={{
          animation:
            "fadeUp 0.4s ease forwards",
          animationDelay: `${
            index * 40
          }ms`,
          opacity: 0,
        }}
        onMouseEnter={() =>
          onPreload(church.id)
        }
        onTouchStart={() =>
          onPreload(church.id)
        }
      >
        <Link
          to={`/churches/${church.id}`}
          className="flex-1 flex items-center gap-3 transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80 min-w-0"
        >
          <ChurchIcon size={20} className="text-[#1b4f9c] dark:text-blue-400 shrink-0" />

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[14px] text-slate-800 dark:text-slate-100 truncate leading-tight">
              {church.name}
            </p>

            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
              {STATUS_LABELS[
                church.status
              ] ||
                church.status ||
                "Sin estado"}
            </p>
          </div>

          {!editMode && (
            <ChevronRight size={16} className="text-slate-300 dark:text-slate-600 shrink-0" />
          )}
        </Link>

        {editMode && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-100">
            <button
              onClick={() =>
                onMove(index, -1)
              }
              disabled={isFirst}
              className="h-11 w-11 flex items-center justify-center text-slate-400 hover:text-[#1b4f9c] disabled:opacity-20 transition-all duration-200 ease-out active:scale-[0.98] active:opacity-70"
            >
              <ArrowUp size={18} />
            </button>

            <button
              onClick={() =>
                onMove(index, 1)
              }
              disabled={isLast}
              className="h-11 w-11 flex items-center justify-center text-slate-400 hover:text-[#1b4f9c] disabled:opacity-20 transition-all duration-200 ease-out active:scale-[0.98] active:opacity-70"
            >
              <ArrowDown size={18} />
            </button>
          </div>
        )}
      </div>
    );
  }
);